import React, { useEffect, useMemo, useRef, useState } from 'react';
import Pagination from 'src/shared/components/pagination/Pagination';
import classNames from 'classnames';
import { Users } from 'lucide-react';

import { Role } from 'src/shared/types';
import { useProjectsQuery, useUserCountSummaryQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { useGetUserManagementMatrixRolesQuery } from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useDebouncedListSearch } from 'src/shared/hooks/useDebouncedListSearch';
import Loader from 'src/shared/components/loader/Loader';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import Input from 'src/shared/components/input/Input';
import { getRoleLabel } from 'src/app/admin/views/user-management/components/userManagementHelpers';
import type { UserProjectAccess } from 'src/app/admin/views/user-management/state/userManagementSlice';
import {
    buildMatrixRowsFromProjectList,
    collectMatrixRoleNamesFromProjects,
    isProjectInAssignableList,
    isRoleUnavailableForProject,
    matrixCellHighlightClass,
    mergeMatrixRoleColumnNames,
    MATRIX_SITES_PAGE_SIZE,
    filterVisibleMatrixRoleColumns,
    roleVisibleInMatrix,
    userCountSummaryToRoleStatsByProject,
    type MatrixRowState,
} from './projectPermissionsMatrixHelpers';
import styles from './ProjectPermissionsMatrix.module.scss';

export type ProjectPermissionsMatrixViewProps = {
    projects: UserProjectAccess[];
    status?: string;
    is_internal_user: boolean;
    /** When false, matrix is greyed out (e.g. inactive user) */
    disabled?: boolean;
    /** Passed to `userManagementMatrixRoles` so column headers match subscription limits for this org. */
    viewTargetUserId?: number;
};

/**
 * Read-only matrix for “View user”: sites where the user already has roles, client-side filter + pagination.
 */
const ProjectPermissionsMatrixView = ({
    projects,
    is_internal_user: isInternalUser,
    disabled = false,
    viewTargetUserId,
}: ProjectPermissionsMatrixViewProps) => {
    const { currentCustomerId, currentCustomerProjectIds } = useCustomer();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { hasProjectLevelRole, hasCustomerLevelRole, hasPermission } = usePermissions();
    const hasPermissionRef = useRef(hasPermission);
    hasPermissionRef.current = hasPermission;
    const { inputValue, activeSearch, onInputChange } = useDebouncedListSearch();

    const { currentData: projectsData } = useProjectsQuery({ customerId: currentCustomerId || '' }, { skip: !currentCustomerId });

    const { currentData: matrixRolesFromApi, isFetching: matrixRolesFetching } = useGetUserManagementMatrixRolesQuery(
        {
            customerId: currentCustomerId || '',
            includeRolesFromUserId: viewTargetUserId,
        },
        { skip: !currentCustomerId }
    );

    /** Only sites the user has at least one role on (not the full assignable catalogue). */
    const scopedProjects = useMemo(
        () => projects.filter(p => p.projectId && currentCustomerProjectIds.includes(p.projectId) && (p.roles?.length ?? 0) > 0),
        [projects, currentCustomerProjectIds]
    );

    const assignableIds = useMemo(() => {
        if (!projectsData) {
            return new Set<string>();
        }
        const set = new Set<string>();
        const check = hasPermissionRef.current;
        projectsData.forEach(p => {
            if (isProjectInAssignableList(p, check)) {
                set.add(p.projectId);
            }
        });
        return set;
    }, [projectsData]);

    const summaryProjectIds = useMemo(() => {
        const ids = scopedProjects.map(p => p.projectId).filter(Boolean) as string[];
        return [...new Set(ids)].sort();
    }, [scopedProjects]);

    const matrixRoleColumnNames = useMemo(() => {
        const merged = mergeMatrixRoleColumnNames(matrixRolesFromApi ?? [], scopedProjects);
        if (merged.length > 0) {
            return merged;
        }
        return collectMatrixRoleNamesFromProjects(scopedProjects);
    }, [matrixRolesFromApi, scopedProjects]);

    /**
     * Row shape without seat counts — used to paginate/filter without fetching summaries for every site.
     * Edit matrix loads counts only for `userManagementProjectList`’s current page; we mirror that by requesting
     * `userCountSummary` only for the project IDs visible on this matrix page (after filter + pagination).
     */
    const rowsWithoutCounts = useMemo(() => {
        if (!projectsData || summaryProjectIds.length === 0) {
            return [] as MatrixRowState[];
        }
        return buildMatrixRowsFromProjectList(projectsData, scopedProjects, assignableIds, matrixRoleColumnNames, undefined);
    }, [projectsData, scopedProjects, summaryProjectIds, assignableIds, matrixRoleColumnNames]);

    const filteredRowsWithoutCounts = useMemo(() => {
        if (!activeSearch) {
            return rowsWithoutCounts;
        }
        const q = activeSearch.toLowerCase();
        return rowsWithoutCounts.filter(r => r.projectName.toLowerCase().includes(q));
    }, [rowsWithoutCounts, activeSearch]);

    const [matrixPage, setMatrixPage] = useState(1);
    const matrixPageCount = Math.max(1, Math.ceil(filteredRowsWithoutCounts.length / MATRIX_SITES_PAGE_SIZE));

    useEffect(() => {
        setMatrixPage(p => Math.min(p, matrixPageCount));
    }, [matrixPageCount]);

    useEffect(() => {
        setMatrixPage(1);
    }, [activeSearch, summaryProjectIds.join(',')]);

    const pageIdsForUserCount = useMemo(() => {
        const start = (matrixPage - 1) * MATRIX_SITES_PAGE_SIZE;
        return filteredRowsWithoutCounts.slice(start, start + MATRIX_SITES_PAGE_SIZE).map(r => r.projectId);
    }, [filteredRowsWithoutCounts, matrixPage]);

    const { currentData: viewUserCountSummaries } = useUserCountSummaryQuery(
        { projectIds: pageIdsForUserCount, includeDisabledUser: false },
        { skip: !currentCustomerId || pageIdsForUserCount.length === 0 }
    );

    /** Full matrix rows; seat-capacity stats only populated for projects returned by the paged `userCountSummary` query. */
    const rows = useMemo(() => {
        if (!projectsData || summaryProjectIds.length === 0) {
            return [] as MatrixRowState[];
        }
        const roleStats = userCountSummaryToRoleStatsByProject(viewUserCountSummaries);
        return buildMatrixRowsFromProjectList(projectsData, scopedProjects, assignableIds, matrixRoleColumnNames, roleStats);
    }, [projectsData, scopedProjects, summaryProjectIds, assignableIds, matrixRoleColumnNames, viewUserCountSummaries]);

    const isCurrentUserCustomerAdmin = hasCustomerLevelRole({
        customerId: currentCustomerId,
        role: Role.CUSTOMER_ADMIN,
    });

    const columnRoles = matrixRoleColumnNames;

    const visibleRoleColumns = useMemo(
        () =>
            filterVisibleMatrixRoleColumns(columnRoles, {
                isInternalUser,
                isCurrentUserCustomerAdmin,
                isCurrentUserSuperAdmin,
                customerProjectIds: currentCustomerProjectIds,
                hasProjectLevelRole,
            }),
        [columnRoles, isInternalUser, isCurrentUserCustomerAdmin, hasProjectLevelRole, isCurrentUserSuperAdmin, currentCustomerProjectIds]
    );

    const filteredRows = useMemo(() => {
        if (!activeSearch) {
            return rows;
        }
        const q = activeSearch.toLowerCase();
        return rows.filter(r => r.projectName.toLowerCase().includes(q));
    }, [rows, activeSearch]);

    const paginatedRows = useMemo(() => {
        const start = (matrixPage - 1) * MATRIX_SITES_PAGE_SIZE;
        return filteredRows.slice(start, start + MATRIX_SITES_PAGE_SIZE);
    }, [filteredRows, matrixPage]);

    if (!projectsData) {
        return (
            <div>
                <Loader />
            </div>
        );
    }

    if (summaryProjectIds.length === 0) {
        return (
            <div className={styles.matrix}>
                <p className={styles.emptyMatrix} role='status'>
                    This user has no site permissions for this organisation.
                </p>
            </div>
        );
    }

    if (matrixRolesFetching && matrixRolesFromApi === undefined) {
        return (
            <div>
                <Loader />
            </div>
        );
    }

    const showEmptyMatrixMessage = filteredRows.length === 0;
    const emptyMatrixMessage =
        rows.length === 0
            ? 'Loading site permissions…'
            : 'No sites match your filter. Adjust or clear the filter to see sites in this list.';

    return (
        <div className={styles.matrix}>
            <div className={styles.matrixToolbar}>
                <div className={styles.filterRow}>
                    <div className={styles.filterLabel}>Filter sites in list</div>
                    <Input placeholder='Type to filter…' value={inputValue} onChange={onInputChange} autoComplete='off' />
                </div>
            </div>
            {showEmptyMatrixMessage ? (
                <p className={styles.emptyMatrix} role='status'>
                    {emptyMatrixMessage}
                </p>
            ) : (
                <>
                    <div className={styles.tableScroll}>
                        <table className={styles.table} data-testid='matrix-permissions-table'>
                            <thead>
                                <tr>
                                    <th scope='col' className={styles.thSite}>
                                        Site
                                    </th>
                                    {visibleRoleColumns.map(roleName => (
                                        <th key={roleName} scope='col' className={styles.thRole}>
                                            <div className={styles.thRoleInner}>
                                                <span>{getRoleLabel(roleName)}</span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedRows.map(row => {
                                    const isCurrentUserProjectAdmin = hasProjectLevelRole({
                                        projectId: row.projectId,
                                        role: Role.PROJECT_ADMIN,
                                    });
                                    return (
                                        <tr key={row.projectId} className={classNames(disabled && styles.rowReadOnly)}>
                                            <th scope='row' className={styles.tdSite}>
                                                {row.projectName}
                                            </th>
                                            {visibleRoleColumns.map(roleName => {
                                                const entry = row.countByRoles.find(c => c.name === roleName);
                                                if (
                                                    !roleVisibleInMatrix(roleName, {
                                                        isInternalUser,
                                                        isCurrentUserCustomerAdmin,
                                                        isCurrentUserProjectAdmin,
                                                        isActorSuperAdmin: isCurrentUserSuperAdmin,
                                                    })
                                                ) {
                                                    return null;
                                                }
                                                if (!entry || isRoleUnavailableForProject(entry)) {
                                                    return (
                                                        <td
                                                            key={roleName}
                                                            className={styles.tdCell}
                                                            data-testid={`matrix-cell-${row.projectId}-${roleName}`}
                                                            data-role-unavailable='true'
                                                        />
                                                    );
                                                }
                                                const highlight = matrixCellHighlightClass(entry);
                                                return (
                                                    <td
                                                        key={roleName}
                                                        className={classNames(
                                                            styles.tdCell,
                                                            highlight === 'error' && styles.tdCellerror,
                                                            highlight === 'warning' && styles.tdCellwarning
                                                        )}
                                                        data-testid={`matrix-cell-${row.projectId}-${roleName}`}
                                                    >
                                                        <div className={styles.cellInner}>
                                                            <Checkbox
                                                                checked={entry.checked}
                                                                onChange={() => {}}
                                                                disabled
                                                                checkboxClassName={styles.matrixCheckbox}
                                                            />
                                                            {entry.roleUserCount !== null && entry.roleUserCount !== undefined ? (
                                                                <span className={styles.userCount}>
                                                                    <Users className={styles.userCountIcon} aria-hidden />
                                                                    {entry.roleUserLimitMax !== null && entry.roleUserLimitMax !== undefined
                                                                        ? `${entry.roleUserCount} / ${entry.roleUserLimitMax}`
                                                                        : entry.roleUserCount}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.matrixPagination}>
                        <span className={styles.matrixPaginationSummary}>
                            Showing {(matrixPage - 1) * MATRIX_SITES_PAGE_SIZE + 1}–
                            {Math.min(matrixPage * MATRIX_SITES_PAGE_SIZE, filteredRows.length)} of {filteredRows.length} sites
                        </span>
                        {matrixPageCount > 1 ? (
                            <Pagination currentPageNumber={matrixPage} lastPageNumber={matrixPageCount} onChange={setMatrixPage} />
                        ) : null}
                    </div>
                </>
            )}
        </div>
    );
};

export default ProjectPermissionsMatrixView;
